<template>
  <main :aria-label="$t('user_group.title')" class="groups-list-page">
    <CompanyBreadcrumb :aria-label="$t('user_group.title')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <div v-if="errorMessages.length" class="card groups-alert-card">
      <div class="uk-alert-danger" uk-alert>
        <template v-for="(error, index) in errorMessages">
          <span v-if="error != null" :key="index">
            <a class="uk-alert-close" uk-close></a>
            <p class="m-0">
              <i class="uil-exclamation-triangle"></i> {{ error }}
            </p>
          </span>
        </template>
      </div>
    </div>
    <div v-if="successMessage" class="card groups-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p><i class="uil-check"></i> {{ successMessage }}</p>
      </div>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle groups-page-header">
      <div class="section-header-left">
        <h2 class="groups-page-title">
          <span>{{ $t("user_group.title") }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button :disabled="!$hasPermission('create_group')"
                :style="{
            cursor: $hasPermission('create_group') ? 'pointer' : 'not-allowed',
          }"
                class="btn btn-default groups-add-btn"
                type="button"
                @click="
            $hasPermission('create_group') &&
              $router.push({ name: 'company.user.group.add' })
          "
        >
          <i class="uil-plus"></i>
          {{ $t("user_group.create") }}
        </button>
      </div>
    </div>

    <div class="card groups-list-card">
      <div class="card-header actions-toolbar border-0 groups-toolbar">
        <div class="d-flex justify-content-between groups-toolbar-inner">
          <div class="groups-toolbar-search">
            <form class="uk-search uk-search-navbar groups-search-field" @submit.prevent>
              <span aria-hidden="true" class="uk-search-icon groups-search-icon">
                <i class="uil-search"></i>
              </span>
              <input v-model="filter.search"
                     :aria-label="$t('general.search')"
                     :placeholder="$t('general.search')"
                     autocomplete="off"
                     class="uk-search-input groups-search-input"
                     type="search"
                     @input="debouncedFilterSearch"
              />
            </form>
          </div>
          <div class="d-flex groups-toolbar-actions">
            <sort-dropdown :sort-options="sortOptions" @sort-selected="sortOrder"/>
            <excel-download-button :loading="isReportDownloading"
                                   :tooltipText="$t('filters.export_excell')"
                                   @download="exportList"/>
          </div>
        </div>
      </div>
      <default-loading v-if="group_loading"></default-loading>
      <template v-else>
        <div class="sf-table sf-table--zebra sf-table--hover groups-table-wrap">
          <table>
            <thead>
            <tr>
              <th scope="col">{{ $t("user_group.list.name") }}</th>
              <th scope="col">{{ $t("user_group.list.user_count") }}</th>
              <th scope="col">{{ $t("general.created_at") }}</th>
              <th scope="col">{{ $t("general.status") }}</th>
              <th class="sf-table__col-actions" scope="col"></th>
            </tr>
            </thead>
            <tbody class="list">
            <tr v-for="item in formattedItem" :key="item.id">
              <th scope="row">
                <div class="sf-table__media">
                  <div>
                    <div class="avatar-parent-child groups-avatar">
                      <!-- rounded-circle YOK: o sinif olmadan bootstrap'in
                           `.avatar.rounded-circle + .avatar-child` kurali eslesmez
                           ve rozet varsayilan `border-radius: 0.2rem`e, yani
                           kareye doner. Gruplar kare, kullanicilar yuvarlak. -->
                      <div class="avatar groups-avatar-inner">
                        <i class="icon-feather-users"></i>
                      </div>
                      <span :class="item.status_id ? 'bg-success' : 'bg-offline'"
                            class="avatar-child avatar-badge"
                      ></span>
                    </div>
                  </div>
                  <div class="sf-table__media-body">
                    <a class="sf-table__name"
                       @click="
                          $router.push({
                            name: 'company.user.group.edit',
                            params: { id: item.id },
                          })
                        "
                    >
                      {{ item.name }}
                    </a>
                  </div>
                </div>
              </th>
              <td>
                <i class="uil-user"></i> {{ item.count }}
              </td>
              <td>
                <i class="uil-calendar-alt"></i> {{ item.created_at }}
              </td>
              <!-- Durum kolonu kurali uygulama genelinde ayni:
                   aktif yesil, pasif kirmizi (bkz. Users/index.vue getUserStatus). -->
              <td :class="item.status_id ? 'sf-table__status--success' : 'sf-table__status--danger'"
                  class="sf-table__status">
                {{
                  item.status_id
                      ? $t("user_group.list.active")
                      : $t("user_group.list.passive")
                }}
              </td>
              <td class="sf-table__col-actions">
                <div class="sf-table__actions">
                  <a :style="{
                        cursor: $hasPermission('create_group')
                          ? 'pointer'
                          : 'not-allowed',
                        opacity: $hasPermission('create_group') ? 1 : 0.5,
                      }"
                     :uk-tooltip="
                        $hasPermission('create_group')
                          ? $t('user_group.add_user_group')
                          : $t('general.no_permission')
                      "
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     @click="
                        $hasPermission('create_group') &&
                          $router.push({
                            name: 'company.user.group.edit',
                            params: { id: item.id },
                          })
                      "
                  >
                    <i class="uil-user-plus"></i>
                  </a>
                  <a :style="{
                        cursor: $hasPermission('create_group')
                          ? 'pointer'
                          : 'not-allowed',
                        opacity: $hasPermission('create_group') ? 1 : 0.5,
                      }"
                     :uk-tooltip="
                        $hasPermission('create_group')
                          ? $t('general.assign_to_group')
                          : $t('general.no_permission')
                      "
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     type="button"
                     @click="
                        $hasPermission('create_group') &&
                          openAssignmentModal(item.id, item.count)
                      "
                  >
                    <i class="uil-location-arrow text-success"></i>
                  </a>
                  <a :style="{
                        cursor: $hasPermission('create_group')
                          ? 'pointer'
                          : 'not-allowed',
                        opacity: $hasPermission('create_group') ? 1 : 0.5,
                      }"
                     :uk-tooltip="
                        $hasPermission('create_group')
                          ? $t('general.update_group')
                          : $t('general.no_permission')
                      "
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     @click="
                        $hasPermission('create_group') &&
                          $router.push({
                            name: 'company.user.group.edit',
                            params: { id: item.id },
                          })
                      "
                  >
                    <i class="uil-pen"></i>
                  </a>
                  <a v-if="item.status_id"
                     :uk-tooltip="$t('general.do_passive')"
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     @click="statusChange(item.id, 0)"
                  >
                    <i class="uil-minus-circle text-warning"></i>
                  </a>
                  <a v-else
                     :uk-tooltip="$t('general.do_active')"
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     @click="statusChange(item.id, 1)"
                  >
                    <i class="uil-check text-success"></i>
                  </a>
                  <a :style="{
                        cursor: $hasPermission('delete_group')
                          ? 'pointer'
                          : 'not-allowed',
                        opacity: $hasPermission('delete_group') ? 1 : 0.5,
                      }"
                     :uk-tooltip="
                        $hasPermission('delete_group')
                          ? $t('general.remove')
                          : $t('general.no_permission')
                      "
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     @click="
                        $hasPermission('delete_group') && removeItem(item.id)
                      "
                  >
                    <i class="uil-trash"></i>
                  </a>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <pagination :pagination="listPagination"
                    class="mt-2 mb-3 groups-pagination"
                    @changePage="setPage($event)"
        ></pagination>
      </template>
    </div>

    <GroupAssignmentModal :group-id="activeGroup"
                          :user-count="userCount"
                          :visible.sync="assignmentModalOpen"
    />
  </main>
</template>
<script>
import module, {
  BASE_URL,
  GET_ITEMS,
  MODULE_NAME,
  ERROR,
  UPDATE_ITEM,
  ITEMS,
  LOADING,
  SUCCESS,
  DELETE_ITEM,
} from "@/core/services/store/user_group.module";
import store from "@/core/services";
import moment from "moment";
import Pagination from "@/assets/components/Pagination";
import DefaultLoading from "@/view/components/loading/DefaultLoading";

import Swal from "sweetalert2";
import ExcelDownloadButton from "@/view/components/buttons/ExcelDownloadButton.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import GroupAssignmentModal from "@/view/company/User/components/GroupAssignmentModal.vue";
import debounce from "lodash/debounce";
import { showError } from "@/utils/alertUtils";
import { downloadFromUrl, exportFileName, readBlobError } from "@/utils/fileDownload";

export default {
  name: "UserGroup",
  components: {
    ExcelDownloadButton,
    Pagination,
    DefaultLoading,
    CompanyBreadcrumb,
    SortDropdown,
    GroupAssignmentModal,
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
  },
  data() {
    return {
      page: 1,
      per_page: 10,
      sort_by: "id",
      sort_desc: "desc",
      activeGroup: null,
      userCount: null,
      filter: {
        search: "",
        order_by: null,
        sort: null,
      },
      successMessage: null,
      errorMessages: [],
      isReportDownloading: false,
      assignmentModalOpen: false,
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("user_group.users") },
        { name: this.$t("user_group.title") },
      ];
    },
    sortOptions() {
      return [
        {
          key: "name-asc",
          sort: "name",
          order: "asc",
          label: this.$t("filters.order.a_z"),
        },
        {
          key: "name-desc",
          sort: "name",
          order: "desc",
          label: this.$t("filters.order.z_a"),
        },
        {
          key: "created_at-asc",
          sort: "created_at",
          order: "asc",
          label: this.$t("filters.order.created_at_asc"),
        },
        {
          key: "created_at-desc",
          sort: "created_at",
          order: "desc",
          label: this.$t("filters.order.created_at_desc"),
        },
      ];
    },
    items() {
      return store.getters[MODULE_NAME + "/" + ITEMS];
    },
    group_loading() {
      return store.getters[MODULE_NAME + "/" + LOADING];
    },
    errors() {
      return store.getters[MODULE_NAME + "/" + ERROR];
    },
    success() {
      return store.getters[MODULE_NAME + "/" + SUCCESS];
    },
    listPagination() {
      if (
          this.items === null ||
          typeof this.items !== "object" ||
          !("data" in this.items)
      ) {
        return null;
      }
      return this.items;
    },
    formattedItem() {
      let results = [];
      if (this.items === null || !("data" in this.items)) return [];

      for (let i = 0; i < this.items.data.length; i++) {
        let currentItem = this.items.data[i];
        if (currentItem) {
          results.push({
            id: currentItem.id,
            name: currentItem.name,
            end_date: currentItem.end_date,
            status_id: currentItem.status,
            count: currentItem.count,
            created_at: currentItem.created_at
                ? moment(currentItem.created_at).format("DD/MM/YYYY HH:mm:ss")
                : "-",
          });
        }
      }
      return results;
    },
  },
  methods: {
    searchGroups() {
      this.page = 1;
      this.filterResult();
    },
    statusChange(id, status_id) {
      store.dispatch(MODULE_NAME + "/" + UPDATE_ITEM, {
        url: BASE_URL + "/status/" + id,
        contents: {
          group: id,
          status_id: status_id,
        },
      });
    },
    openAssignmentModal(groupId, userCount) {
      this.activeGroup = groupId;
      this.userCount = userCount;
      this.assignmentModalOpen = true;
    },
    deleteUserGroup(id) {
      this.$store
          .dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
            url: BASE_URL + "/" + id,
          })
          .then(() => {
            if (!this.error) {
              this.successMessage = this.$t("general.successfully_deleted");
              this.filterResult();
            } else {
              this.errorMessages.push(this.error);
            }
          });
    },
    removeItem(id) {
      let self = this;
      Swal.fire({
        title: this.$t("general.delete_group_confirm"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("general.yes"),
        cancelButtonText: this.$t("general.cancel"),
        confirmButtonColor: "#e31748",
      }).then((result) => {
        if (result.isConfirmed) {
          self.deleteUserGroup(id);
        }
      });
      return false;
    },
    filterResult() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          like: this.filter.search || null,
          page: this.page,
          per_page: this.per_page,
          order_by: this.filter.order_by,
          sort: this.filter.sort,
        },
      });
    },
    sortOrder({ sort, order }) {
      this.page = 1;
      this.filter.order_by = sort;
      this.filter.sort = order;
      this.filterResult();
    },
    setPage(page) {
      this.page = page;
      this.filterResult();
    },
    // İndirme web/native ayrımı utils/fileDownload.js içinde yapılır;
    // burada sadece "ne indirilecek" tarif edilir.
    async exportList(
        status_id = null,
        url = "/api/group/export",
        file_name = "group_list"
    ) {
      if (this.isReportDownloading) {
        return;
      }
      this.isReportDownloading = true;

      try {
        await downloadFromUrl({
          url: process.env.VUE_APP_BACKEND_APP_URL + url,
          params: status_id != null ? { status_id } : null,
          headers: this.apiHeaders,
          fileName: exportFileName(file_name),
        });
      } catch (error) {
        // Eskiden hata sessizce yutuluyordu. responseType 'blob' olduğu için
        // hata gövdesi de Blob gelir; readBlobError gerçek mesajı çıkarır.
        showError(await readBlobError(error));
      } finally {
        this.isReportDownloading = false;
      }
    },
  },
  created() {
    this.debouncedFilterSearch = debounce(this.searchGroups, 500);
  },
  mounted() {
    this.filterResult();
  },
  beforeDestroy() {
    if (this.debouncedFilterSearch) {
      this.debouncedFilterSearch.cancel();
    }
  },
  watch: {
    success: function (val) {
      if (val) {
        this.filterResult();
      }
    },
  },
};
</script>
<style scoped>
.groups-list-page {
  --groups-primary: var(--primaryColor, #3e416d);
  --groups-text: #1e293b;
  --groups-muted: #64748b;
  --groups-border: #e2e8f0;
  --groups-soft: #f6f7fb;
  min-width: 0;
}

.groups-alert-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--groups-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.groups-list-page .uk-alert {
  margin: 0;
}

.groups-list-page .uk-alert-danger p {
  margin: 0;
  color: #dc3545;
  font-size: 0.875rem;
  line-height: 1.5;
}

.groups-list-page .uk-alert-success p {
  margin: 0;
  color: #28a745;
  font-size: 0.875rem;
  line-height: 1.5;
}

.groups-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.groups-page-header .section-header-right > * {
  margin-left: 0;
}

.groups-page-title {
  margin: 0;
  color: var(--groups-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.groups-add-btn {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.groups-add-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.groups-add-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.groups-list-card {
  overflow: hidden;
  margin-top: 0;
  border: 1px solid var(--groups-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.groups-toolbar {
  padding: 16px;
}

.groups-toolbar-inner {
  align-items: center;
  gap: 16px;
}

.groups-toolbar-search {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 480px;
}

.groups-search-field,
.groups-search-field.uk-search-navbar {
  display: block;
  position: relative;
  width: 100%;
  max-width: 100%;
}

.groups-search-icon,
.groups-list-page ::v-deep .groups-search-icon.uk-search-icon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  margin: 0;
  pointer-events: none;
  color: #3f87f5;
  font-size: 20px;
  line-height: 1;
}

.groups-search-icon i.uil-search {
  position: static;
  top: auto;
  left: auto;
  font-size: 20px;
  line-height: 1;
}

.groups-search-input,
.groups-toolbar-search .uk-search-input {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 16px 10px 40px !important;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  background: #fff;
  color: var(--groups-text);
  font-size: 14px !important;
  line-height: 1.4;
}

.groups-search-input:focus-visible,
.groups-toolbar-search .uk-search-input:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.25);
  outline-offset: 2px;
}

.groups-toolbar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.groups-toolbar-actions ::v-deep .btn-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.groups-toolbar-actions ::v-deep .btn-circle:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* Tablo tasarimi .sf-table'dan gelir (assets/scss/_data-table.scss).
   Burada yalnizca sayfaya ozgu degerler ayarlanir. */

/* Rozet kalibi /admin/user/list (Users) sayfasiyla ayni.
   Boyut ve konum bootstrap'in `.avatar + .avatar-badge` zincirinden gelir;
   bunun calismasi icin ikonun `.avatar` sarmalayicisi olmak zorunda —
   kardes dogrudan `<i>` oldugunda kural eslesmiyor ve rozet boyutsuz kaliyor.

   Tek fark: burada `rounded-circle` kullanilmiyor, bu yuzden hem avatar
   hem rozet kare (Users'ta yuvarlak). */
.groups-avatar {
  width: max-content;
}

.groups-avatar-inner {
  width: 32px;
  height: 32px;
}

/* global `.avatar-parent-child i { font-size: 24px; padding: 13px }`
   kutuyu 50px'e sisiriyordu; sifirlanmazsa rozet ikondan uzaga dusuyor */
.groups-avatar-inner i {
  padding: 0;
  font-size: 24px;
  line-height: 1;
}

.groups-pagination {
  padding: 0 8px 8px;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

@media (max-width: 959px) {
  .groups-list-page {
    padding: 0 0 24px;
  }

  .groups-page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .groups-page-header .section-header-left,
  .groups-page-header .section-header-right {
    width: 100%;
  }

  .groups-page-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
  }

  .groups-list-page .uk-alert-danger p,
  .groups-list-page .uk-alert-success p {
    font-size: 16px;
  }

  .groups-add-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .groups-toolbar-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .groups-toolbar-search {
    width: 100%;
    max-width: none;
  }

  .groups-search-icon,
  .groups-list-page ::v-deep .groups-search-icon.uk-search-icon {
    width: 48px;
  }

  .groups-search-input,
  .groups-toolbar-search .uk-search-input {
    height: 48px;
    min-height: 48px;
    padding: 12px 16px 12px 48px !important;
    font-size: 16px !important;
  }

  .groups-toolbar-actions {
    flex-wrap: wrap;
    align-self: flex-end;
    width: auto;
    max-width: 100%;
    margin-left: auto;
    justify-content: flex-end;
  }

  .groups-toolbar-actions ::v-deep .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }

  .groups-list-page ::v-deep .sort-dropdown .uk-dropdown-nav > li > a {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  /* 5 kolonlu tablo mobilde sikistirilmaz, yatay kaydirilir */
  .groups-table-wrap {
    --sf-table-min-width: 880px;
  }
}

@media (max-width: 767px) {
  .groups-list-page {
    padding: 0;
  }

  .groups-page-title {
    font-size: 24px;
  }

  .groups-pagination {
    padding: 0 16px 16px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .groups-add-btn:hover:not(:disabled) {
    opacity: 0.92;
  }
}

@media (hover: none) {
  .groups-list-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .groups-add-btn:active,
  .groups-toolbar-actions ::v-deep .btn-circle:active {
    transform: none;
  }
}
</style>
